import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Atom, Cpu, Shield, Lock, Brain, CheckCircle, ArrowRight } from 'lucide-react';

interface SolutionsProps {
  setCurrentPage?: (page: string) => void;
}

const Solutions: React.FC<SolutionsProps> = ({ setCurrentPage }) => {
  const solutions = [
    {
      icon: Zap,
      title: "Pulse Cybersecurity Solution",
      description: "Essential security features for small to medium businesses",
      features: [
        "Basic threat monitoring",
        "Firewall protection",
        "Email security",
        "Access control"
      ],
      color: "blue",
      link: "pulse-security"
    },
    {
      icon: Atom,
      title: "Neutron Cybersecurity Solution",
      description: "Advanced protection for growing enterprises",
      features: [
        "24/7 threat monitoring",
        "Advanced endpoint protection",
        "Cloud security",
        "AI-powered analytics"
      ],
      color: "purple",
      link: "neutron-security"
    },
    {
      icon: Cpu,
      title: "Quantum Cybersecurity Package",
      description: "Enterprise-grade security for large organizations",
      features: [
        "Zero-trust architecture",
        "Custom security solutions",
        "Advanced AI capabilities",
        "Global security operations"
      ],
      color: "green",
      link: "quantum-security"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Comprehensive Protection",
      description: "Multi-layered security approach protecting your entire digital infrastructure"
    },
    {
      icon: Lock,
      title: "Advanced Security",
      description: "State-of-the-art security technologies and methodologies"
    },
    {
      icon: Brain,
      title: "Intelligent Defense",
      description: "AI-powered security solutions for proactive threat prevention"
    }
  ];

  const handleLearnMore = (page: string) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
  };

  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute w-96 h-96 -right-48 top-0 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute w-96 h-96 -left-48 bottom-0 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Comprehensive Security Solutions</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-muted max-w-2xl mx-auto"
          >
            Choose the security package that best fits your organization's needs
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 auto-rows-fr">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card flex flex-col"
            >
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                <feature.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 flex-none">{feature.title}</h3>
              <p className="text-muted flex-grow">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 auto-rows-fr">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card flex flex-col relative"
            >
              <div className={`flex items-center justify-center w-20 h-20 bg-gradient-to-br from-${solution.color}-500 to-${solution.color}-600 rounded-2xl mb-8 animate-float`}>
                <solution.icon className="h-10 w-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold mb-4">
                {solution.title}
              </h3>
              
              <p className="text-muted mb-8">
                {solution.description}
              </p>
              
              <ul className="space-y-4 mb-8 flex-grow">
                {solution.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <CheckCircle className={`h-5 w-5 text-${solution.color}-500 mr-3 flex-shrink-0`} />
                    <span className="text-muted">{feature}</span>
                  </li>
                ))}
              </ul>

              {setCurrentPage && (
                <button 
                  onClick={() => handleLearnMore(solution.link)}
                  className={`w-full py-4 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center group ${
                    solution.color === 'blue' ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' :
                    solution.color === 'purple' ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' :
                    'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                  }`}
                >
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-20"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Future?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts to discuss your organization's needs and discover the right solution for you
            </p>
            <button 
              onClick={() => handleLearnMore('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;